import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Save, Plus, Edit2, Pause, Play, Archive, 
  RotateCcw, Trash2, Upload, MapPin, DollarSign, Calendar,
  Clock, Tag, AlertCircle, CheckCircle, Image as ImageIcon, X, Loader2,
  Star, Search, ExternalLink, RefreshCw, Link2, Unlink, Copy, Globe, QrCode, Download, User
} from 'lucide-react';
import { BACKEND_URL, resolveAssetUrl, flag } from '../../config';
import AdminLayout from '../../components/admin/AdminLayout';
import { formatDateEST } from '../../utils/dateUtils';
import { adminApi } from '../../lib/api';
import { CATEGORY_LIST, getSubcategories } from '../../config/businessCategories';

export default function AdminBusinessEdit() {
  const { businessId } = useParams();
  const navigate = useNavigate();
  
  const [business, setBusiness] = useState(null);
  const [deals, setDeals] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('profile'); // 'profile', 'google', 'hubs', 'owner', 'deals'
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Profile form state
  const [profileForm, setProfileForm] = useState({
    name: '',
    description: '',
    category: '',
    subcategory: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    website: '',
    hub_ids: []
  });
  
  // Deal form state
  const [showDealForm, setShowDealForm] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [dealForm, setDealForm] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    generate_lead: false
  });
  
  const [message, setMessage] = useState({ type: '', text: '' });
  const [viewArchived, setViewArchived] = useState(false);
  
  // Google Places state
  const [googlePlaceSearch, setGooglePlaceSearch] = useState('');
  const [googleSearchResults, setGoogleSearchResults] = useState([]);
  const [googleSearching, setGoogleSearching] = useState(false);
  const [googleUpdating, setGoogleUpdating] = useState(false);
  const [showGoogleSearch, setShowGoogleSearch] = useState(false);
  
  // Verification state
  const [verifying, setVerifying] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  
  // QR Code state
  const [qrLoading, setQrLoading] = useState(false);
  
  // Invite User state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  
  // Pending Invite state
  const [pendingInvite, setPendingInvite] = useState(null);
  const [loadingInvite, setLoadingInvite] = useState(false);
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
  const [newInviteEmail, setNewInviteEmail] = useState('');
  const [inviteActionLoading, setInviteActionLoading] = useState(false);

  useEffect(() => {
    loadBusinessData();
    loadLocations();
  }, [businessId]);
  
  // Load pending invite when business loads and has no owner
  useEffect(() => {
    if (business && !business.owner_id) {
      loadPendingInvite();
    } else {
      setPendingInvite(null);
    }
  }, [business?.id, business?.owner_id]);
  
  const loadPendingInvite = async () => {
    try {
      setLoadingInvite(true);
      const response = await adminApi.get(
        `/admin/businesses/${businessId}/pending-invite`
      );
      if (response.data.has_pending_invite) {
        setPendingInvite(response.data.invite);
      } else {
        setPendingInvite(null);
      }
    } catch (error) {
      console.error('Error loading pending invite:', error);
      setPendingInvite(null);
    } finally {
      setLoadingInvite(false);
    }
  };

  const loadBusinessData = async () => {
    try {
      
      const businessRes = await adminApi.get(
        `/admin/businesses/${businessId}`, { validateStatus: () => true }
      );
      
      if (businessRes.status < 200 || businessRes.status >= 300) {
        console.error('Business fetch error:', businessRes.status);
        throw new Error(`Failed to load business: HTTP ${businessRes.status}`);
      }
      
      const businessData = businessRes.data;
      setBusiness(businessData);
      
      // Set image preview with loading state
      if (businessData.image_url) {
        setImageLoading(true);
        setImagePreview(businessData.image_url);
      } else {
        setImagePreview(null);
      }
      
      // Set profile form - handle both old and new field names
      setProfileForm({
        name: businessData.name || '',
        description: businessData.description || '',
        category: businessData.category || '',
        subcategory: businessData.subcategory || '',
        street_address: businessData.street_address || businessData.address || '',
        city: businessData.city || '',
        state: businessData.state || '',
        zip_code: businessData.zip_code || '',
        phone: businessData.phone || '',
        website: businessData.website || '',
        hub_ids: businessData.hub_ids || []
      });
      
      try {
        const dealsRes = await adminApi.get(
          `/admin/businesses/${businessId}/deals`, { validateStatus: () => true }
        );
        
        if (dealsRes.status >= 200 && dealsRes.status < 300) {
          setDeals(dealsRes.data || []);
        } else {
          // Non-200 response - log but don't fail (business might not have deals yet)
          console.warn(`Failed to load deals for business ${businessId}: ${dealsRes.status}`);
          setDeals([]);
        }
      } catch (dealError) {
        // Error loading deals - log but don't fail the whole page
        console.warn('Error loading deals:', dealError);
        setDeals([]);
      }
      
    } catch (error) {
      console.error('Error loading business:', error);
      setMessage({ type: 'error', text: 'Failed to load business data. Check console for details.' });
    } finally {
      setLoading(false);
    }
  };

  const loadLocations = async () => {
    try {
      const response = await adminApi.get(`/locations`, { validateStatus: () => true });
      if (response.status >= 200 && response.status < 300) {
        setLocations(response.data);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Target dimensions (1920x1080 max)
          let width = img.width;
          let height = img.height;
          const maxWidth = 1920;
          const maxHeight = 1080;
          
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = width * ratio;
            height = height * ratio;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to blob with compression
          canvas.toBlob(
            (blob) => {
              resolve(new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              }));
            },
            'image/jpeg',
            0.85 // 85% quality
          );
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      setMessage({ type: 'error', text: 'Please upload a JPG, PNG, or WebP image' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      return;
    }

    setUploading(true);
    setMessage({ type: '', text: '' });
    
    try {
      // Compress image before uploading
      const compressedFile = await compressImage(file);
      
      // Check compressed file size (should be under 2MB after compression)
      if (compressedFile.size > 2 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'Image too large even after compression. Please use a smaller image.' });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        setUploading(false);
        return;
      }

      const formData = new FormData();
      formData.append('file', compressedFile);

      const response = await adminApi.post(
        `/admin/businesses/upload-image`,
        formData,
        { timeout: 30000 }
      );

      const uploadedImageUrl = response.data.image_url;
      
      const patchResponse = await adminApi.patch(
        `/admin/businesses/${businessId}`,
        { image_url: uploadedImageUrl }, { validateStatus: () => true }
      );
      
      if (patchResponse.status < 200 || patchResponse.status >= 300) {
        throw new Error('Failed to save image to business profile');
      }
      
      // Add cache buster to force browser to load fresh image
      const cacheBustedUrl = `${uploadedImageUrl}?t=${Date.now()}`;
      setImageLoading(true); // Show loading while new image loads
      setImagePreview(cacheBustedUrl);
      
      // Also update the business state to keep in sync
      setBusiness(prev => prev ? { ...prev, image_url: uploadedImageUrl } : prev);
      
      setMessage({ type: 'success', text: 'Image uploaded and saved successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('Error uploading image:', error);
      let errorMessage = 'Failed to upload image';
      
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        errorMessage = 'Network error during upload. Please check your connection and try again.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.status === 403) {
        errorMessage = 'Access denied. Please log in again.';
      }
      
      setMessage({ type: 'error', text: errorMessage });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = async () => {
    if (!confirm('Remove business image?')) return;

    try {
      const response = await adminApi.patch(
        `/admin/businesses/${businessId}`,
        { image_url: null }, { validateStatus: () => true }
      );
      
      if (response.status < 200 || response.status >= 300) {
        throw new Error('Failed to remove image from business profile');
      }
      
      setImagePreview(null);
      setBusiness(prev => prev ? { ...prev, image_url: null } : prev);
      setMessage({ type: 'success', text: 'Image removed successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('Error removing image:', error);
      setMessage({ type: 'error', text: 'Failed to remove image' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  const handleProfileUpdate = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    
    try {
      const response = await adminApi.patch(
        `/admin/businesses/${businessId}`,
        profileForm, { validateStatus: () => true }
      );
      
      if (response.status < 200 || response.status >= 300) {
        const errorDetail = response.data?.detail || 'Failed to update business';
        throw new Error(errorDetail);
      }
      
      setMessage({ type: 'success', text: 'Business profile updated successfully!' });
      await loadBusinessData();
    } catch (error) {
      console.error('Error updating business:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to update business profile' });
    } finally {
      setSaving(false);
    }
  };

  const handleDealSubmit = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    
    try {
      const url = editingDeal 
        ? `/admin/businesses/${businessId}/deals/${editingDeal.id}`
        : `/admin/businesses/${businessId}/deals`;
      
      const method = editingDeal ? 'put' : 'post';
      
      // Clean up empty date fields to avoid validation errors
      const dealData = {
        title: dealForm.title,
        description: dealForm.description,
        business_id: businessId,
        generate_lead: dealForm.generate_lead
      };
      // Only include dates if they have values
      if (dealForm.start_date) dealData.start_date = dealForm.start_date;
      if (dealForm.end_date) dealData.end_date = dealForm.end_date;
      
      const response = await adminApi({
        method,
        url,
        data: dealData,
        validateStatus: () => true, // Don't throw on non-2xx
      });
      
      if (response.status < 200 || response.status >= 300) {
        let errorMessage = `Failed to save deal (Error ${response.status})`;
        if (response.data?.detail) {
          // Handle both string and array detail responses (FastAPI validation errors)
          if (typeof response.data.detail === 'string') {
            errorMessage = response.data.detail;
          } else if (Array.isArray(response.data.detail)) {
            // Extract first validation error message
            const firstError = response.data.detail[0];
            errorMessage = firstError?.msg || firstError?.message || 'Validation error';
          }
        } else {
          const statusMessages = {
            400: 'Invalid deal data provided',
            401: 'Session expired. Please log in again',
            403: 'You do not have permission to manage this deal',
            404: 'Deal or business not found',
            422: 'Please check all required fields are filled correctly',
            500: 'Server error. Please try again'
          };
          errorMessage = statusMessages[response.status] || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      // Success
      setMessage({ type: 'success', text: `Deal ${editingDeal ? 'updated' : 'created'} successfully!` });
      setShowDealForm(false);
      setEditingDeal(null);
      setDealForm({ title: '', description: '', start_date: '', end_date: '', generate_lead: false });
      await loadBusinessData();
    } catch (error) {
      console.error('Error saving deal:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save deal' });
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePause = async (dealId, currentStatus) => {
    try {
      await adminApi.patch(
        `/admin/businesses/${businessId}/deals/${dealId}/pause`,
        {}
      );

      await loadBusinessData();
    } catch (error) {
      console.error('Error toggling pause:', error);
    }
  };

  const handleArchiveDeal = async (dealId) => {
    try {
      await adminApi.patch(
        `/admin/businesses/${businessId}/deals/${dealId}/archive`,
        {}
      );

      setMessage({ type: 'success', text: 'Deal archived successfully' });
      await loadBusinessData();
    } catch (error) {
      console.error('Error archiving deal:', error);
    }
  };

  const handleRestoreDeal = async (dealId) => {
    try {
      await adminApi.patch(
        `/admin/businesses/${businessId}/deals/${dealId}/restore`,
        {}
      );

      setMessage({ type: 'success', text: 'Deal restored successfully' });
      await loadBusinessData();
    } catch (error) {
      console.error('Error restoring deal:', error);
    }
  };

  const openEditDeal = (deal) => {
    setEditingDeal(deal);
    setDealForm({
      title: deal.title,
      description: deal.description,
      start_date: deal.start_date || '',
      end_date: deal.end_date || '',
      generate_lead: deal.generate_lead || false
    });
    setShowDealForm(true);
  };

  // Google Places Functions
  const searchGooglePlaces = async () => {
    if (!googlePlaceSearch.trim()) return;
    
    setGoogleSearching(true);
    setGoogleSearchResults([]);
    
    try {
      const response = await adminApi.post(
        `/google-ratings/find-place-id`,
        {
          business_name: googlePlaceSearch,
          city: profileForm.city || '',
          state: profileForm.state || ''
        },
        { validateStatus: () => true }
      );
      
      const data = response.data;
      
      if (data.found && data.place_id) {
        // Get rating details for the found place
        setGoogleSearchResults([{
          place_id: data.place_id,
          name: data.name,
          formatted_address: data.formatted_address
        }]);
      } else {
        setGoogleSearchResults([]);
        setMessage({ type: 'error', text: 'No Google Places found for this search' });
      }
    } catch (error) {
      console.error('Error searching Google Places:', error);
      setMessage({ type: 'error', text: 'Failed to search Google Places' });
    } finally {
      setGoogleSearching(false);
    }
  };

  const linkGooglePlace = async (placeId) => {
    setGoogleUpdating(true);
    
    try {
      const response = await adminApi.put(
        `/google-ratings/set-place-id/${businessId}`,
        { place_id: placeId },
        { validateStatus: () => true }
      );
      
      const data = response.data;
      
      if (data.status === 'success') {
        setMessage({ type: 'success', text: `Linked to Google Places! Rating: ${data.google_rating} (${data.google_total_reviews} reviews)` });
        setShowGoogleSearch(false);
        setGoogleSearchResults([]);
        setGooglePlaceSearch('');
        await loadBusinessData();
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to link Google Place' });
      }
    } catch (error) {
      console.error('Error linking Google Place:', error);
      setMessage({ type: 'error', text: 'Failed to link Google Place' });
    } finally {
      setGoogleUpdating(false);
    }
  };

  const refreshGoogleRating = async () => {
    if (!business?.google_place_id) return;
    
    setGoogleUpdating(true);
    
    try {
      const response = await adminApi.post(
        `/google-ratings/update/${businessId}`,
        {},
        { validateStatus: () => true }
      );
      
      const data = response.data;
      
      if (data.status === 'success') {
        setMessage({ type: 'success', text: `Rating updated: ${data.google_rating} (${data.google_total_reviews} reviews)` });
        await loadBusinessData();
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to refresh rating' });
      }
    } catch (error) {
      console.error('Error refreshing rating:', error);
      setMessage({ type: 'error', text: 'Failed to refresh Google rating' });
    } finally {
      setGoogleUpdating(false);
    }
  };

  const unlinkGooglePlace = async () => {
    if (!window.confirm('Are you sure you want to remove the Google Places link? The rating will no longer be displayed.')) {
      return;
    }
    
    setGoogleUpdating(true);
    
    try {
      await adminApi.put(
        `/admin/businesses/${businessId}`,
        {
          google_place_id: null,
          google_rating: null,
          google_total_reviews: null,
          google_rating_updated_at: null
        }
      );
      
      setMessage({ type: 'success', text: 'Google Places link removed' });
      await loadBusinessData();
    } catch (error) {
      console.error('Error unlinking Google Place:', error);
      setMessage({ type: 'error', text: 'Failed to remove Google Places link' });
    } finally {
      setGoogleUpdating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setMessage({ type: 'success', text: 'Copied to clipboard!' });
    setTimeout(() => setMessage({ type: '', text: '' }), 2000);
  };

  // Generate QR code for business
  const handleGenerateQR = async () => {
    setQrLoading(true);
    try {
      const response = await adminApi.post(
        `/admin/businesses/${businessId}/generate-qr`,
        {}
      );
      if (response.data.success) {
        // Add cache-busting timestamp to force image reload
        const newUrl = `${response.data.qr_code_url}?t=${Date.now()}`;
        setBusiness(prev => ({ ...prev, instore_qr_code_url: newUrl }));
        setMessage({ type: 'success', text: 'QR code generated successfully!' });
      }
    } catch (error) {
      console.error('Error generating QR:', error);
      setMessage({ type: 'error', text: 'Failed to generate QR code' });
    } finally {
      setQrLoading(false);
    }
  };

  // Download QR code
  const handleDownloadQR = () => {
    if (!business?.instore_qr_code_url) return;
    const link = document.createElement('a');
    // Remove cache-busting parameter for download
    const cleanUrl = business.instore_qr_code_url.split('?')[0];
    link.href = resolveAssetUrl(cleanUrl);
    link.download = `${business.slug || businessId}-instore-qr.png`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return formatDateEST(dateString);
  };

  // Verify Business Handler
  const handleVerifyBusiness = async () => {
    setVerifying(true);
    try {
      await adminApi.post(
        `/admin/businesses/${businessId}/verify`,
        {}
      );
      
      setMessage({ type: 'success', text: 'Business approved successfully! Owner has been notified.' });
      setBusiness(prev => ({ ...prev, verification_status: 'approved', is_active: true }));
      setShowApproveModal(false);
    } catch (error) {
      console.error('Error verifying business:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to verify business' });
    } finally {
      setVerifying(false);
    }
  };

  // Reject Business Handler
  const handleRejectBusiness = async () => {
    if (!rejectReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    
    setVerifying(true);
    try {
      await adminApi.post(
        `/admin/businesses/${businessId}/reject`,
        { reason: rejectReason }
      );
      
      setMessage({ type: 'success', text: 'Business rejected. Owner has been notified.' });
      setBusiness(prev => ({ ...prev, verification_status: 'rejected', verification_note: rejectReason }));
      setShowRejectModal(false);
      setRejectReason('');
    } catch (error) {
      console.error('Error rejecting business:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to reject business' });
    } finally {
      setVerifying(false);
    }
  };

  // Send Invite to User Handler
  const handleSendInvite = async () => {
    if (!inviteEmail.trim()) {
      setMessage({ type: 'error', text: 'Please enter an email address' });
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      setMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }
    
    setInviteSending(true);
    try {
      const response = await adminApi.post(
        `/admin/businesses/${businessId}/invite-user`,
        { email: inviteEmail }
      );
      
      setMessage({ type: 'success', text: response.data.message || 'Invitation sent successfully!' });
      setShowInviteModal(false);
      setInviteEmail('');
      
      // Reload business data and pending invite
      await loadBusinessData();
      await loadPendingInvite();
    } catch (error) {
      console.error('Error sending invite:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to send invitation' });
    } finally {
      setInviteSending(false);
    }
  };
  
  // Resend pending invite
  const handleResendInvite = async () => {
    setInviteActionLoading(true);
    try {
      const response = await adminApi.post(
        `/admin/businesses/${businessId}/resend-invite`,
        {}
      );
      setMessage({ type: 'success', text: response.data.message || 'Invitation resent!' });
      loadPendingInvite();
    } catch (error) {
      console.error('Error resending invite:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to resend invitation' });
    } finally {
      setInviteActionLoading(false);
    }
  };
  
  // Cancel pending invite
  const handleCancelInvite = async () => {
    if (!window.confirm('Are you sure you want to cancel this invitation?')) return;
    
    setInviteActionLoading(true);
    try {
      await adminApi.delete(
        `/admin/businesses/${businessId}/cancel-invite`
      );
      setMessage({ type: 'success', text: 'Invitation cancelled' });
      setPendingInvite(null);
    } catch (error) {
      console.error('Error cancelling invite:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to cancel invitation' });
    } finally {
      setInviteActionLoading(false);
    }
  };
  
  // Update invite email
  const handleUpdateInviteEmail = async () => {
    if (!newInviteEmail.trim()) {
      setMessage({ type: 'error', text: 'Please enter an email address' });
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newInviteEmail)) {
      setMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }
    
    setInviteActionLoading(true);
    try {
      const response = await adminApi.patch(
        `/admin/businesses/${businessId}/update-invite-email`,
        { email: newInviteEmail }
      );
      setMessage({ type: 'success', text: response.data.message || 'Email updated and invitation sent!' });
      setShowChangeEmailModal(false);
      setNewInviteEmail('');
      loadPendingInvite();
    } catch (error) {
      console.error('Error updating invite email:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to update email' });
    } finally {
      setInviteActionLoading(false);
    }
  };
  
  // Format date helper
  const formatInviteDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return formatDateEST(dateStr);
  };

  const activeDeals = deals.filter(d => !d.archived);
  const archivedDeals = deals.filter(d => d.archived);
  const displayDeals = viewArchived ? archivedDeals : activeDeals;

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-business border-t-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* V5 Back Link */}
        <button
          onClick={() => navigate('/admin/businesses')}
          className="flex items-center gap-2 text-navy/60 hover:text-navy transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Businesses
        </button>
        
        {/* V5 Page Header with Invite User Button */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="font-mono text-business text-sm mb-1">// admin.businesses.edit</p>
            <h1 className="font-display text-3xl text-navy">
              Edit Business: {business?.name}
            </h1>
          </div>
          
          {/* Invite User Button - Only show if no owner attached AND no pending invite */}
          {!business?.owner_id && !pendingInvite && !loadingInvite && (
            <button
              onClick={() => setShowInviteModal(true)}
              className="px-4 py-2 bg-business text-white rounded-lg hover:bg-business/90 transition-colors flex items-center gap-2"
            >
              <Plus size={18} />
              Invite User
            </button>
          )}
        </div>
        
        {/* Pending Invitation Card - Show when there's a pending invite */}
        {!business?.owner_id && pendingInvite && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock size={24} className="text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-navy text-lg flex items-center gap-2">
                    Pending Invitation
                    {pendingInvite.is_expired && (
                      <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full">Expired</span>
                    )}
                  </h3>
                  <div className="mt-2 space-y-1 text-sm">
                    <p className="text-navy/70">
                      <span className="font-medium text-navy">Email:</span>{' '}
                      <span className="font-mono bg-white/50 px-2 py-0.5 rounded">{pendingInvite.email}</span>
                    </p>
                    <p className="text-navy/70">
                      <span className="font-medium text-navy">Sent:</span>{' '}
                      {formatInviteDate(pendingInvite.created_at)}
                    </p>
                    <p className="text-navy/70">
                      <span className="font-medium text-navy">Expires:</span>{' '}
                      <span className={pendingInvite.is_expired ? 'text-red-600 font-medium' : ''}>
                        {formatInviteDate(pendingInvite.expires_at)}
                      </span>
                    </p>
                    <p className="text-navy/70">
                      <span className="font-medium text-navy">Type:</span>{' '}
                      {pendingInvite.invite_type === 'link_existing' ? 'Existing User' : 'New User'}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    setNewInviteEmail(pendingInvite.email);
                    setShowChangeEmailModal(true);
                  }}
                  disabled={inviteActionLoading}
                  className="px-3 py-2 bg-white border border-navy/20 text-navy rounded-lg hover:bg-navy/5 transition-colors flex items-center gap-2 text-sm font-medium disabled:opacity-50"
                >
                  <Edit2 size={16} />
                  Change Email
                </button>
                <button
                  onClick={handleResendInvite}
                  disabled={inviteActionLoading}
                  className="px-3 py-2 bg-business text-white rounded-lg hover:bg-business/90 transition-colors flex items-center gap-2 text-sm font-medium disabled:opacity-50"
                >
                  {inviteActionLoading ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <RefreshCw size={16} />
                  )}
                  Resend
                </button>
                <button
                  onClick={handleCancelInvite}
                  disabled={inviteActionLoading}
                  className="px-3 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-2 text-sm font-medium disabled:opacity-50"
                >
                  <X size={16} />
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Verification Status Banner */}
        {business?.verification_status === 'pending' && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <Clock size={20} className="text-amber-600" />
                </div>
                <div>
                  <p className="font-semibold text-amber-800">⏳ Pending Verification</p>
                  <p className="text-sm text-amber-700">This business is waiting for admin approval to go live.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowApproveModal(true)}
                  disabled={verifying}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  <CheckCircle size={18} />
                  Approve Business
                </button>
                <button
                  onClick={() => setShowRejectModal(true)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                >
                  <X size={18} />
                  Reject
                </button>
              </div>
            </div>
          </div>
        )}

        {business?.verification_status === 'rejected' && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-3">
              <AlertCircle size={20} className="text-red-600" />
              <div>
                <p className="font-semibold text-red-800">❌ Business Rejected</p>
                <p className="text-sm text-red-700">Reason: {business.verification_note || 'No reason provided'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Message */}
        <AnimatePresence>
          {message.text && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
                message.type === 'success' 
                  ? 'bg-green-50 text-green-800 border border-green-200' 
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}
            >
              {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
              <p>{message.text}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* V5 Tabs */}
        <div className="bg-white rounded-2xl shadow-sm border border-navy/5 mb-6">
          <div className="relative border-b border-navy/5">
            {/* right-edge fade: on a narrow screen only two tabs fit, and nothing
                else hints that the strip scrolls */}
            <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-white sm:hidden" aria-hidden="true" />
            <nav className="flex -mb-px overflow-x-auto">
              <button
                onClick={() => setActiveTab('profile')}
                className={`px-3 sm:px-5 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'profile'
                    ? 'border-business text-business'
                    : 'border-transparent text-navy/60 hover:text-navy hover:border-navy/20'
                }`}
              >
                Profile Settings
              </button>
              <button
                onClick={() => setActiveTab('google')}
                className={`px-3 sm:px-5 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'google'
                    ? 'border-business text-business'
                    : 'border-transparent text-navy/60 hover:text-navy hover:border-navy/20'
                }`}
              >
                Google Places
              </button>
              <button
                onClick={() => setActiveTab('hubs')}
                className={`px-3 sm:px-5 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'hubs'
                    ? 'border-business text-business'
                    : 'border-transparent text-navy/60 hover:text-navy hover:border-navy/20'
                }`}
              >
                Hub Locations ({business?.hub_ids?.length || 0})
              </button>
              <button
                onClick={() => setActiveTab('owner')}
                className={`px-3 sm:px-5 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-2 ${
                  activeTab === 'owner'
                    ? 'border-business text-business'
                    : 'border-transparent text-navy/60 hover:text-navy hover:border-navy/20'
                }`}
              >
                Business Owner
                {!business?.owner_id && (
                  <span className="w-2 h-2 rounded-full bg-amber-400"></span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('deals')}
                className={`px-3 sm:px-5 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'deals'
                    ? 'border-business text-business'
                    : 'border-transparent text-navy/60 hover:text-navy hover:border-navy/20'
                }`}
              >
                Manage Deals ({activeDeals.length})
              </button>
            </nav>
          </div>
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="bg-white rounded-2xl shadow-sm border border-navy/5 overflow-hidden">
            {/* V5 Section Header with Gradient */}
            <div className="bg-gradient-to-r from-business to-purple-400 px-8 py-6">
              <h2 className="font-display text-xl text-white">Business Profile</h2>
              <p className="text-white/70 text-sm mt-1">Update your business information</p>
            </div>
            
            <div className="p-8 space-y-8">
              {/* Business Image Upload Section */}
              <div className="space-y-6">
                <div className="flex items-center gap-2 pb-2 border-b border-navy/10">
                  <ImageIcon size={18} className="text-navy/40" />
                  <h3 className="font-semibold text-navy">Business Image</h3>
                </div>
                
                <div>
                  <p className="text-xs text-gray-500 mb-3">
                    Recommended: 16:9 aspect ratio (1920x1080). Images will be automatically optimized. This appears on deal cards and business pages.
                  </p>
                  
                  {imagePreview ? (
                    <div className="relative group">
                      {/* Loading overlay */}
                      {imageLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-xl border-2 border-gray-200 z-10">
                          <div className="flex flex-col items-center gap-2">
                            <Loader2 size={32} className="animate-spin text-[#6F5BEB]" />
                            <span className="text-sm text-gray-500">Loading image...</span>
                          </div>
                        </div>
                      )}
                      <img
                        src={(() => {
                          // Handle various URL formats
                          let url = imagePreview;
                          // Remove any existing cache buster to avoid double-adding
                          const baseUrl = url.split('?')[0];
                          const cacheBuster = url.includes('?') ? '' : `?t=${Date.now()}`;
                          
                          if (baseUrl.startsWith('http')) {
                            return `${baseUrl}${cacheBuster}`;
                          } else if (baseUrl.startsWith('/api')) {
                            return `${BACKEND_URL}${baseUrl}${cacheBuster}`;
                          } else {
                            return `${BACKEND_URL}${baseUrl}${cacheBuster}`;
                          }
                        })()}
                        alt="Business"
                        className={`w-full h-64 object-cover rounded-xl border-2 border-gray-200 transition-opacity ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
                        onError={(e) => {
                          setImageLoading(false);
                          console.error('Image failed to load:', e.target.src);
                          // Try reloading once with fresh cache buster
                          const currentSrc = e.target.src;
                          const baseSrc = currentSrc.split('?')[0];
                          if (!e.target.dataset.retried) {
                            e.target.dataset.retried = 'true';
                            setImageLoading(true);
                            e.target.src = `${baseSrc}?retry=${Date.now()}`;
                          } else {
                            // Show placeholder on second failure
                            e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect fill="%23f3f4f6" width="400" height="300"/><text x="50%" y="50%" text-anchor="middle" fill="%239ca3af" font-size="14">Image unavailable</text></svg>';
                          }
                        }}
                        onLoad={() => {
                          setImageLoading(false);
                          console.log('Image loaded successfully');
                        }}
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-3">
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading}
                          className="px-4 py-2 bg-[#6366F1] text-white rounded-lg font-semibold hover:bg-[#5558D9] transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Upload size={16} />
                          Change
                        </button>
                        <button
                          type="button"
                          onClick={handleRemoveImage}
                          disabled={uploading}
                          className="px-4 py-2 bg-[#EF4444] text-white rounded-lg font-semibold hover:bg-[#DC2626] transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <X size={16} />
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full h-64 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-[#6F5BEB] hover:bg-purple-50 transition-all"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="animate-spin mb-2" size={32} style={{ color: '#6F5BEB' }} />
                          <p className="text-sm text-gray-600">Uploading...</p>
                        </>
                      ) : (
                        <>
                          <ImageIcon size={32} className="mb-2" style={{ color: '#9CA3AF' }} />
                          <p className="text-sm font-semibold" style={{ color: '#111827' }}>
                            Click to upload business image
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            JPG, PNG, or WebP • Auto-optimized for web
                          </p>
                        </>
                      )}
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>
              </div>

              {/* V5 Basic Info Section */}
              <div className="space-y-6">
                <div className="flex items-center gap-2 pb-2 border-b border-navy/10">
                  <Tag size={18} className="text-navy/40" />
                  <h3 className="font-semibold text-navy">Basic Information</h3>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">Business Name *</label>
                  <input
                    type="text"
                    value={profileForm.name}
                    onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                    className="w-full px-4 py-3 bg-cream border border-navy/10 rounded-xl text-navy placeholder-navy/40 focus:outline-none focus:border-business transition-colors"
                    placeholder="Enter business name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy mb-2">Description</label>
                  <textarea
                    value={profileForm.description}
                    onChange={(e) => setProfileForm({ ...profileForm, description: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-3 bg-cream border border-navy/10 rounded-xl text-navy placeholder-navy/40 focus:outline-none focus:border-business transition-colors"
                    placeholder="Tell customers about your business..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy mb-2">
                    <div className="flex items-center gap-2">
                      <Tag size={14} className="text-navy/40" />
                      Business Category
                    </div>
                  </label>
                  <select
                    value={profileForm.category}
                    onChange={(e) => setProfileForm({ ...profileForm, category: e.target.value, subcategory: '' })}
                    className="w-full px-4 py-3 bg-cream border border-navy/10 rounded-xl text-navy focus:outline-none focus:border-business transition-colors"
                  >
                    <option value="">Select a category...</option>
                    {CATEGORY_LIST.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                {/* Subcategory dropdown */}
                {profileForm.category && getSubcategories(profileForm.category).length > 0 && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      <div className="flex items-center gap-2">
                        <Tag size={14} />
                        Business Type (Subcategory)
                      </div>
                    </label>
                    <select
                      value={profileForm.subcategory}
                      onChange={(e) => setProfileForm({ ...profileForm, subcategory: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#6F5BEB] focus:border-transparent transition-shadow bg-white"
                    >
                      <option value="">Select a specific type...</option>
                      {getSubcategories(profileForm.category).map((sub) => (
                        <option key={sub} value={sub}>{sub}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Address Section */}
              <div className="space-y-6">
                <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                  <MapPin size={18} className="text-[#6F5BEB]" />
                  <h3 className="text-lg font-semibold text-gray-900">Business Address</h3>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Street Address
                  </label>
                  <input
                    type="text"
                    value={profileForm.street_address}
                    onChange={(e) => setProfileForm({ ...profileForm, street_address: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#6F5BEB] focus:border-transparent transition-shadow"
                    placeholder="123 Main Street"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      City
                    </label>
                    <input
                      type="text"
                      value={profileForm.city}
                      onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#6F5BEB] focus:border-transparent transition-shadow"
                      placeholder="Medford"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      State
                    </label>
                    <input
                      type="text"
                      value={profileForm.state}
                      onChange={(e) => setProfileForm({ ...profileForm, state: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#6F5BEB] focus:border-transparent transition-shadow"
                      placeholder="NJ"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      ZIP Code
                    </label>
                    <input
                      type="text"
                      value={profileForm.zip_code}
                      onChange={(e) => setProfileForm({ ...profileForm, zip_code: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#6F5BEB] focus:border-transparent transition-shadow"
                      placeholder="08055"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Section */}
              <div className="space-y-6">
                <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                  <Clock size={18} className="text-[#6F5BEB]" />
                  <h3 className="text-lg font-semibold text-gray-900">Contact Information</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#6F5BEB] focus:border-transparent transition-shadow"
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Website
                    </label>
                    <input
                      type="url"
                      value={profileForm.website}
                      onChange={(e) => setProfileForm({ ...profileForm, website: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#6F5BEB] focus:border-transparent transition-shadow"
                      placeholder="https://www.example.com"
                    />
                  </div>
                </div>
              </div>

              {/* Save Profile Button */}
              <div className="flex justify-end pt-6 border-t border-gray-200">
                <button
                  onClick={handleProfileUpdate}
                  disabled={saving}
                  className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-[#6F5BEB] to-[#8B5CF6] text-white rounded-xl font-semibold hover:opacity-90 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={20} />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Google Places Tab */}
        {activeTab === 'google' && (
          <div className="bg-white rounded-2xl shadow-sm border border-navy/5 overflow-hidden">
            <div className="bg-gradient-to-r from-green-500 to-emerald-400 px-8 py-6">
              <h2 className="font-display text-xl text-white">Google Places Integration</h2>
              <p className="text-white/70 text-sm mt-1">Manage Google Reviews and Places ID</p>
            </div>
            
            <div className="p-8 space-y-8">
              {/* Google Reviews & Places ID Section */}
              <div className="space-y-6">
                {/* Current Status */}
                {business?.google_place_id ? (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <CheckCircle size={20} className="text-green-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-green-800">Linked to Google Places</span>
                            {business.google_rating && (
                              <div className="flex items-center gap-1 bg-amber-100 px-2 py-0.5 rounded-full">
                                <Star size={14} className="text-amber-500 fill-amber-500" />
                                <span className="text-sm font-medium text-amber-700">
                                  {business.google_rating.toFixed(1)}
                                </span>
                                {business.google_total_reviews > 0 && (
                                  <span className="text-xs text-amber-600">
                                    ({business.google_total_reviews.toLocaleString()} reviews)
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          <p className="text-sm text-green-600">
                            Last updated: {formatDate(business.google_rating_updated_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={refreshGoogleRating}
                          disabled={googleUpdating}
                          className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                          title="Refresh rating"
                        >
                          <RefreshCw size={18} className={googleUpdating ? 'animate-spin' : ''} />
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                        <AlertCircle size={20} className="text-amber-600" />
                      </div>
                      <div>
                        <span className="font-semibold text-amber-800">No Google Place ID Linked</span>
                        <p className="text-sm text-amber-600">
                          Link this business to Google Places to display ratings and reviews.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Current Place ID Display */}
                {business?.google_place_id && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Current Place ID
                    </label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-mono text-gray-600 truncate">
                        {business.google_place_id}
                      </code>
                      <button
                        onClick={() => copyToClipboard(business.google_place_id)}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Copy Place ID"
                      >
                        <Copy size={18} />
                      </button>
                      <a
                        href={`https://www.google.com/maps/place/?q=place_id:${business.google_place_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        title="View on Google Maps"
                      >
                        <ExternalLink size={18} />
                      </a>
                    </div>
                  </div>
                )}

                {/* Search & Link Section */}
                <div className="border border-gray-200 rounded-xl p-4">
                  <button
                    onClick={() => setShowGoogleSearch(!showGoogleSearch)}
                    className="flex items-center gap-2 text-[#6F5BEB] font-medium hover:text-[#5B4AC4] transition-colors"
                  >
                    <Search size={18} />
                    {business?.google_place_id ? 'Change Google Places Link' : 'Search & Link Google Place'}
                  </button>
                  
                  {showGoogleSearch && (
                    <div className="mt-4 space-y-4">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={googlePlaceSearch}
                          onChange={(e) => setGooglePlaceSearch(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && searchGooglePlaces()}
                          placeholder="Search by business name..."
                          className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#6F5BEB] focus:border-transparent"
                        />
                        <button
                          onClick={searchGooglePlaces}
                          disabled={googleSearching || !googlePlaceSearch.trim()}
                          className="px-4 py-2 bg-[#6F5BEB] text-white rounded-lg hover:bg-[#5B4AC4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {googleSearching ? (
                            <Loader2 size={18} className="animate-spin" />
                          ) : (
                            <Search size={18} />
                          )}
                          Search
                        </button>
                      </div>
                      
                      <p className="text-xs text-gray-500">
                        Tip: Search uses business name + city ({profileForm.city || 'N/A'}, {profileForm.state || 'N/A'})
                      </p>

                      {/* Search Results */}
                      {googleSearchResults.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-gray-700">Search Results:</p>
                          {googleSearchResults.map((result) => (
                            <div
                              key={result.place_id}
                              className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:border-[#6F5BEB] transition-colors"
                            >
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 truncate">{result.name}</p>
                                <p className="text-sm text-gray-500 truncate">{result.formatted_address}</p>
                              </div>
                              <button
                                onClick={() => linkGooglePlace(result.place_id)}
                                disabled={googleUpdating}
                                className="ml-3 px-3 py-1.5 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 flex items-center gap-1"
                              >
                                {googleUpdating ? (
                                  <Loader2 size={14} className="animate-spin" />
                                ) : (
                                  <Link2 size={14} />
                                )}
                                Link
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Manual Place ID Entry */}
                      <div className="pt-4 border-t border-gray-200">
                        <p className="text-sm font-medium text-gray-700 mb-2">Or enter Place ID manually:</p>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder="ChIJ..."
                            id="manualPlaceId"
                            className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#6F5BEB] focus:border-transparent font-mono text-sm"
                          />
                          <button
                            onClick={() => {
                              const input = document.getElementById('manualPlaceId');
                              if (input.value.trim()) {
                                linkGooglePlace(input.value.trim());
                              }
                            }}
                            disabled={googleUpdating}
                            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
                          >
                            Link
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Remove Link Button */}
                {business?.google_place_id && (
                  <button
                    onClick={unlinkGooglePlace}
                    disabled={googleUpdating}
                    className="flex items-center gap-2 text-red-500 hover:text-red-600 text-sm font-medium transition-colors"
                  >
                    <Unlink size={16} />
                    Remove Google Places Link
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Hub Locations Tab */}
        {activeTab === 'hubs' && (
          <div className="bg-white rounded-2xl shadow-sm border border-navy/5 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-500 to-purple-400 px-8 py-6">
              <h2 className="font-display text-xl text-white">Hub Locations</h2>
              <p className="text-white/70 text-sm mt-1">Manage where this business appears</p>
            </div>
            
            <div className="p-8 space-y-8">
              {flag('INSTANT_REDEEM') && (<>
              {/* In-Store QR Code Section */}
              <div className="space-y-6">
                <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                  <QrCode size={18} className="text-[#6F5BEB]" />
                  <h3 className="text-lg font-semibold text-gray-900">In-Store QR Code</h3>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-6">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                    {/* Left side - Info & Actions */}
                    <div className="flex-1">
                      <p className="text-sm text-gray-600 mb-4">
                        Generate a QR code for this business to use on door signage. When scanned, customers will be taken directly to the instant redemption page.
                      </p>
                      
                      <div className="flex flex-wrap items-center gap-2">
                        {business?.instore_qr_code_url ? (
                          <>
                            <button
                              onClick={handleDownloadQR}
                              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white"
                              style={{ background: 'linear-gradient(135deg, #6F5BEB 0%, #5B4BC9 100%)' }}
                            >
                              <Download size={16} />
                              Download QR
                            </button>
                            <button
                              onClick={handleGenerateQR}
                              disabled={qrLoading}
                              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50"
                            >
                              {qrLoading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                              Regenerate
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={handleGenerateQR}
                            disabled={qrLoading}
                            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white"
                            style={{ background: 'linear-gradient(135deg, #6F5BEB 0%, #5B4BC9 100%)' }}
                          >
                            {qrLoading ? (
                              <>
                                <Loader2 size={16} className="animate-spin" />
                                Generating...
                              </>
                            ) : (
                              <>
                                <QrCode size={16} />
                                Generate QR Code
                              </>
                            )}
                          </button>
                        )}
                      </div>
                      
                      {business?.instore_qr_generated_at && (
                        <p className="text-xs text-gray-400 mt-3">
                          Last generated: {formatDate(business.instore_qr_generated_at)}
                        </p>
                      )}
                    </div>
                    
                    {/* Right side - QR Preview */}
                    {business?.instore_qr_code_url && (
                      <div className="flex-shrink-0">
                        <div className="w-32 h-32 bg-white rounded-xl flex items-center justify-center overflow-hidden border-2 border-gray-200">
                          <img 
                            src={resolveAssetUrl(business.instore_qr_code_url)}
                            alt="In-Store QR Code"
                            className="w-full h-full object-contain p-2"
                          />
                        </div>
                        <p className="text-center text-xs text-gray-400 mt-2">Preview</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              </>)}
              {/* Hub Locations Section */}
              <div className="space-y-6">
                <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                  <MapPin size={18} className="text-[#6F5BEB]" />
                  <h3 className="text-lg font-semibold text-gray-900">Select Hub Locations</h3>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Choose which hubs this business should appear in
                  </label>
                  <div className="space-y-2 border border-gray-200 rounded-xl p-4 max-h-80 overflow-y-auto bg-gray-50">
                    {locations.length === 0 ? (
                      <p className="text-gray-500 text-sm py-4 text-center">No hub locations available</p>
                    ) : (
                      locations.map(loc => (
                        <label
                          key={loc.id}
                          className="flex items-center gap-3 p-3 rounded-lg hover:bg-white border border-transparent hover:border-purple-200 cursor-pointer transition-all group"
                        >
                          <input
                            type="checkbox"
                            checked={profileForm.hub_ids.includes(loc.id)}
                            onChange={(e) => {
                              const newHubIds = e.target.checked
                                ? [...profileForm.hub_ids, loc.id]
                                : profileForm.hub_ids.filter(id => id !== loc.id);
                              setProfileForm({ ...profileForm, hub_ids: newHubIds });
                            }}
                            className="w-5 h-5 rounded border-gray-300 text-[#6F5BEB] focus:ring-[#6F5BEB] cursor-pointer"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900 group-hover:text-[#6F5BEB]">
                              {loc.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {loc.city}, {loc.state}
                            </div>
                          </div>
                          {profileForm.hub_ids.includes(loc.id) && (
                            <div className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded-full">
                              Selected
                            </div>
                          )}
                        </label>
                      ))
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {profileForm.hub_ids.length} location{profileForm.hub_ids.length !== 1 ? 's' : ''} selected
                  </p>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end pt-6 border-t border-gray-200">
                <button
                  onClick={handleProfileUpdate}
                  disabled={saving}
                  className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-[#6F5BEB] to-[#8B5CF6] text-white rounded-xl font-semibold hover:opacity-90 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={20} />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Business Owner Tab */}
        {activeTab === 'owner' && (
          <div className="bg-white rounded-2xl shadow-sm border border-navy/5 overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-orange-400 px-8 py-6">
              <h2 className="font-display text-xl text-white">Business Owner</h2>
              <p className="text-white/70 text-sm mt-1">Manage who controls this business</p>
            </div>
            
            <div className="p-8">
              {/* Current Owner Status */}
              {business?.owner_id ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <User size={24} className="text-green-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-green-800 text-lg">Owner Assigned</h3>
                      <p className="text-green-700 mt-1">
                        <span className="font-medium">{business?.owner_email}</span>
                      </p>
                      <p className="text-green-600 text-sm mt-2">
                        This user has full access to manage deals, view analytics, and update business information.
                      </p>
                    </div>
                  </div>
                </div>
              ) : pendingInvite ? (
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6">
                  <div className="flex items-start justify-between flex-wrap gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Clock size={24} className="text-amber-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-navy text-lg flex items-center gap-2">
                          Pending Invitation
                          {pendingInvite.is_expired && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full">Expired</span>
                          )}
                        </h3>
                        <div className="mt-3 space-y-2 text-sm">
                          <p className="text-navy/70">
                            <span className="font-medium text-navy">Email:</span>{' '}
                            <span className="font-mono bg-white/50 px-2 py-0.5 rounded">{pendingInvite.email}</span>
                          </p>
                          <p className="text-navy/70">
                            <span className="font-medium text-navy">Sent:</span>{' '}
                            {formatInviteDate(pendingInvite.created_at)}
                          </p>
                          <p className="text-navy/70">
                            <span className="font-medium text-navy">Expires:</span>{' '}
                            <span className={pendingInvite.is_expired ? 'text-red-600 font-medium' : ''}>
                              {formatInviteDate(pendingInvite.expires_at)}
                            </span>
                          </p>
                          <p className="text-navy/70">
                            <span className="font-medium text-navy">Type:</span>{' '}
                            {pendingInvite.invite_type === 'link_existing' ? 'Existing User' : 'New User'}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => {
                          setNewInviteEmail(pendingInvite.email);
                          setShowChangeEmailModal(true);
                        }}
                        disabled={inviteActionLoading}
                        className="px-4 py-2.5 bg-white border border-navy/20 text-navy rounded-lg hover:bg-navy/5 transition-colors flex items-center gap-2 text-sm font-medium disabled:opacity-50"
                      >
                        <Edit2 size={16} />
                        Change Email
                      </button>
                      <button
                        onClick={handleResendInvite}
                        disabled={inviteActionLoading}
                        className="px-4 py-2.5 bg-business text-white rounded-lg hover:bg-business/90 transition-colors flex items-center gap-2 text-sm font-medium disabled:opacity-50"
                      >
                        {inviteActionLoading ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <RefreshCw size={16} />
                        )}
                        Resend
                      </button>
                      <button
                        onClick={handleCancelInvite}
                        disabled={inviteActionLoading}
                        className="px-4 py-2.5 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-2 text-sm font-medium disabled:opacity-50"
                      >
                        <X size={16} />
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <User size={24} className="text-gray-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-800 text-lg">No Owner Assigned</h3>
                      <p className="text-gray-600 mt-1">
                        This business doesn't have an owner yet. Invite someone to manage it.
                      </p>
                      <button
                        onClick={() => setShowInviteModal(true)}
                        className="mt-4 px-4 py-2.5 bg-business text-white rounded-lg hover:bg-business/90 transition-colors flex items-center gap-2 text-sm font-medium"
                      >
                        <Plus size={16} />
                        Invite User to Manage
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Deals Tab */}
        {activeTab === 'deals' && (
          <div className="space-y-6">
            {/* Deal Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Deals Management</h2>
                <div className="flex gap-3">
                  <button
                    onClick={() => setViewArchived(!viewArchived)}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    {viewArchived ? <RotateCcw size={18} /> : <Archive size={18} />}
                    {viewArchived ? 'View Active' : 'View Archived'}
                  </button>
                  <button
                    onClick={() => {
                      setEditingDeal(null);
                      setDealForm({ title: '', description: '', start_date: '', end_date: '', generate_lead: false });
                      setShowDealForm(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#FF7A1A] to-[#6F5BEB] text-white rounded-lg font-semibold hover:opacity-90"
                  >
                    <Plus size={18} />
                    Create Deal
                  </button>
                </div>
              </div>

              {/* Deal Form */}
              {showDealForm && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="border-t pt-6 mt-6"
                >
                  <h3 className="text-lg font-semibold mb-4">
                    {editingDeal ? 'Edit Deal' : 'Create New Deal'}
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Deal Title *
                      </label>
                      <input
                        type="text"
                        value={dealForm.title}
                        onChange={(e) => setDealForm({ ...dealForm, title: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7A1A] focus:border-transparent"
                        placeholder="e.g., 20% Off Craft Beers"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description *
                      </label>
                      <textarea
                        value={dealForm.description}
                        onChange={(e) => setDealForm({ ...dealForm, description: e.target.value })}
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7A1A] focus:border-transparent"
                        placeholder="Describe your deal..."
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Start Date (Optional)
                        </label>
                        <input
                          type="datetime-local"
                          value={dealForm.start_date}
                          onChange={(e) => setDealForm({ ...dealForm, start_date: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7A1A] focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          End Date (Optional)
                        </label>
                        <input
                          type="datetime-local"
                          value={dealForm.end_date}
                          onChange={(e) => setDealForm({ ...dealForm, end_date: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7A1A] focus:border-transparent"
                        />
                      </div>
                    </div>

                    {/* Lead Generation Toggle */}
                    <div className="mt-4 p-4 rounded-lg" style={{ backgroundColor: dealForm.generate_lead ? 'rgba(255, 122, 26, 0.05)' : 'rgba(0,0,0,0.02)', border: dealForm.generate_lead ? '2px solid #FF7A1A' : '2px solid rgba(0,0,0,0.1)' }}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">Lead Generation</p>
                          <p className="text-sm text-gray-500">Capture customer info when they claim this deal</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setDealForm({ ...dealForm, generate_lead: !dealForm.generate_lead })}
                          className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out"
                          style={{ backgroundColor: dealForm.generate_lead ? '#FF7A1A' : 'rgba(0,0,0,0.2)' }}
                        >
                          <span 
                            className="inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out"
                            style={{ transform: dealForm.generate_lead ? 'translateX(22px)' : 'translateX(2px)', marginTop: '2px' }}
                          />
                        </button>
                      </div>
                      {dealForm.generate_lead && (
                        <p className="mt-2 text-sm text-orange-600">
                          ✓ Business owner will receive email notifications with lead details
                        </p>
                      )}
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={handleDealSubmit}
                        disabled={saving || !dealForm.title || !dealForm.description}
                        className="px-6 py-2 bg-gradient-to-r from-[#FF7A1A] to-[#6F5BEB] text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-50"
                      >
                        {saving ? 'Saving...' : editingDeal ? 'Update Deal' : 'Create Deal'}
                      </button>
                      <button
                        onClick={() => {
                          setShowDealForm(false);
                          setEditingDeal(null);
                        }}
                        className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Deals List */}
              <div className="mt-6 space-y-4">
                {displayDeals.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">
                    {viewArchived ? 'No archived deals' : 'No active deals'}
                  </p>
                ) : (
                  displayDeals.map(deal => (
                    <div
                      key={deal.id}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900">{deal.title}</h3>
                          <p className="text-gray-600 text-sm mt-1">{deal.description}</p>
                          
                          <div className="flex flex-wrap gap-3 mt-3">
                            {deal.is_paused && (
                              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                                Paused
                              </span>
                            )}
                            {deal.archived && (
                              <span className="px-3 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">
                                Archived
                              </span>
                            )}
                            {deal.generate_lead && (
                              <span className="px-3 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                                🎯 Lead Gen
                              </span>
                            )}
                            <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                              {deal.total_claims || 0} claims
                            </span>
                            <span className="px-3 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                              {deal.total_redemptions || 0} redeemed
                            </span>
                          </div>
                        </div>

                        <div className="flex gap-2 ml-4">
                          {!deal.archived && (
                            <>
                              <button
                                onClick={() => openEditDeal(deal)}
                                className="p-2 hover:bg-gray-100 rounded-lg"
                                title="Edit"
                              >
                                <Edit2 size={18} className="text-blue-600" />
                              </button>
                              <button
                                onClick={() => handleTogglePause(deal.id, deal.is_paused)}
                                className="p-2 hover:bg-gray-100 rounded-lg"
                                title={deal.is_paused ? 'Resume' : 'Pause'}
                              >
                                {deal.is_paused ? (
                                  <Play size={18} className="text-green-600" />
                                ) : (
                                  <Pause size={18} className="text-yellow-600" />
                                )}
                              </button>
                              <button
                                onClick={() => handleArchiveDeal(deal.id)}
                                className="p-2 hover:bg-gray-100 rounded-lg"
                                title="Archive"
                              >
                                <Archive size={18} className="text-gray-600" />
                              </button>
                            </>
                          )}
                          {deal.archived && (
                            <button
                              onClick={() => handleRestoreDeal(deal.id)}
                              className="p-2 hover:bg-gray-100 rounded-lg"
                              title="Restore"
                            >
                              <RotateCcw size={18} className="text-green-600" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Approve Modal */}
      <AnimatePresence>
        {showApproveModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowApproveModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
              onClick={e => e.stopPropagation()}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={32} className="text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Approve Business</h3>
              </div>
              
              <p className="text-gray-600 mb-2 text-center">
                Are you sure you want to approve <strong>{business?.name}</strong>?
              </p>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-green-800">
                  <strong>This will:</strong>
                </p>
                <ul className="text-sm text-green-700 mt-2 space-y-1">
                  <li>• Activate the business profile</li>
                  <li>• Make their deals visible to customers</li>
                  <li>• Grant the owner full dashboard access</li>
                  <li>• Send a congratulations email to the owner</li>
                </ul>
              </div>
              
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowApproveModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleVerifyBusiness}
                  disabled={verifying}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2 transition-colors"
                >
                  {verifying ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Approving...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={18} />
                      Approve Business
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reject Modal */}
      <AnimatePresence>
        {showRejectModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowRejectModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
              onClick={e => e.stopPropagation()}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <X size={32} className="text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Reject Business Application</h3>
              </div>
              
              <p className="text-gray-600 mb-4 text-center">
                Please provide a reason for rejecting <strong>{business?.name}</strong>. This will be sent to the business owner.
              </p>
              
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter rejection reason..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 mb-4"
                rows={4}
              />
              
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectReason('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectBusiness}
                  disabled={verifying || !rejectReason.trim()}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2 transition-colors"
                >
                  {verifying ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Rejecting...
                    </>
                  ) : (
                    <>
                      <X size={18} />
                      Reject Business
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
        
        {/* Invite User Modal */}
        {showInviteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowInviteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-xl text-navy">Invite New User</h3>
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X size={20} className="text-gray-500" />
                </button>
              </div>
              
              <p className="text-navy/70 text-sm mb-4">
                Enter the email address of the user you want to invite to manage <strong>{business?.name}</strong>.
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-navy mb-2">Email Address</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="user@example.com"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-business focus:ring-2 focus:ring-business/20 outline-none transition-all"
                  autoFocus
                />
              </div>
              
              <p className="text-xs text-navy/50 mb-6">
                If the user already has an account, they will receive a confirmation email. 
                Otherwise, they will be invited to create an account. The invite expires in 7 days.
              </p>
              
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendInvite}
                  disabled={inviteSending || !inviteEmail.trim()}
                  className="px-4 py-2 bg-business text-white rounded-lg hover:bg-business/90 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {inviteSending ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Plus size={18} />
                      Send Invite
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
        
        {/* Change Invite Email Modal */}
        {showChangeEmailModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowChangeEmailModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-xl text-navy">Change Invite Email</h3>
                <button
                  onClick={() => setShowChangeEmailModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X size={20} className="text-gray-500" />
                </button>
              </div>
              
              <p className="text-navy/70 text-sm mb-4">
                Update the email address for the invitation to manage <strong>{business?.name}</strong>.
                A new invitation will be sent to this address.
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-navy mb-2">New Email Address</label>
                <input
                  type="email"
                  value={newInviteEmail}
                  onChange={(e) => setNewInviteEmail(e.target.value)}
                  placeholder="user@example.com"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-business focus:ring-2 focus:ring-business/20 outline-none transition-all"
                  autoFocus
                />
              </div>
              
              <p className="text-xs text-navy/50 mb-6">
                The previous invitation will be invalidated and a new one will be sent.
              </p>
              
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowChangeEmailModal(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateInviteEmail}
                  disabled={inviteActionLoading || !newInviteEmail.trim()}
                  className="px-4 py-2 bg-business text-white rounded-lg hover:bg-business/90 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {inviteActionLoading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <RefreshCw size={18} />
                      Update & Send
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AdminLayout>
  );
}
